// Library Methods
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";

// Components
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import { AlertFileIcon, DownloadIcon, ViewFileIcon } from "src/assets/icons";

// Types
import { ProgressionAnalysisType } from "src/services/exams/types";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getSurfaceTokens,
  noHoverBackgroundSx,
} from "src/styles/mui/muiStylesHelper";

// Utils
import { getLocalDate } from "src/utils/date/dateHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import useOnDemandProgressionReportGeneration from "src/hooks/reports/useOnDemandProgressionReportGeneration";

type ExtendedAnalysisType = ProgressionAnalysisType & {
  earliestCompletionDate: string;
  latestCompletionDate: string;
};

type ProgressionAnalysisCardType = {
  progressionAnalysis: ExtendedAnalysisType;
  patientId: string;
  isSelectedForDeletion: boolean;
  handleCheckboxClick: (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => void;
};

const ProgressionAnalysisCard = ({
  progressionAnalysis,
  patientId,
  isSelectedForDeletion,
  handleCheckboxClick,
}: ProgressionAnalysisCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const navigate = useNavigate();

  const isSelectedForViewing = false;

  const theme = useTheme();
  const { elevatedSurface } = getSurfaceTokens(theme);
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const analysisHref = useMemo(
    () =>
      `/patients/${patientId}/progression-analysis/${progressionAnalysis.id}`,
    [patientId, progressionAnalysis.id]
  );

  const isAnalysisValid = useMemo(
    () => progressionAnalysis.state === "Valid",
    [progressionAnalysis.state]
  );

  // TODO: enable progression downloads from the card
  const canDownload = useMemo(
    () => false,
    // isAnalysisValid &&
    // Boolean(
    //   progressionAnalysis.exams?.length > 0 ||
    //     progressionAnalysis.externalExams?.length > 0
    // ),
    [
      // isAnalysisValid,
      // progressionAnalysis.exams,
      // progressionAnalysis.externalExams,
    ]
  );

  const { downloadFile } = useOnDemandProgressionReportGeneration({
    patientId,
    analysisId: progressionAnalysis.id,
  });

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    await downloadFile();
    setIsDownloading(false);
  };

  const completionDateRange = useMemo(
    () =>
      `${progressionAnalysis.earliestCompletionDate} - ${progressionAnalysis.latestCompletionDate}`,
    [
      progressionAnalysis.earliestCompletionDate,
      progressionAnalysis.latestCompletionDate,
    ]
  );

  const creationDate = useMemo(
    () => getLocalDate(progressionAnalysis.creationDate, clinicInfo?.timezone),
    [progressionAnalysis.creationDate, clinicInfo?.timezone]
  );

  return (
    <TableCard
      key={progressionAnalysis.id}
      isSelectedForViewing={isSelectedForViewing}
      isSelectedForDeletion={isSelectedForDeletion}
      onClick={() => {}}
      hasHoverState={false}
      variant="dark"
      backgroundColorOverride={
        theme.palette.mode === "dark" ? elevatedSurface : undefined
      }
    >
      <Grid container display="flex" alignItems="center">
        <Grid container item xs={12} display="flex" alignItems="center">
          <Grid item xs={1}>
            <CustomCheckbox
              id={`progression-analysis-checkbox-${progressionAnalysis.id}`}
              checkedFill={
                isSelectedForViewing || isSelectedForDeletion
                  ? "white"
                  : "#202338"
              }
              checkedStroke={
                isSelectedForViewing || isSelectedForDeletion
                  ? "#202338"
                  : "white"
              }
              checked={isSelectedForDeletion}
              onClick={(
                event: React.MouseEvent<HTMLButtonElement, MouseEvent>
              ) => {
                event.stopPropagation();
                handleCheckboxClick(event, progressionAnalysis.id);
              }}
            />
          </Grid>

          <Grid container item xs={9} display="flex" alignItems="center">
            <Grid
              data-testid={`progression-analysis-eye-${progressionAnalysis?.id}`}
              item
              xs={12}
              sm={2.7}
            >
              <Tooltip title={t(progressionAnalysis?.eye)}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>{t("Eye")}: </span>
                  )}
                  {t(progressionAnalysis?.eye)}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid
              data-testid={`progression-analysis-date-range-${progressionAnalysis?.id}`}
              item
              xs={12}
              sm={6.6}
            >
              <Tooltip title={completionDateRange}>
                <Typography
                  whiteSpace="noWrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>
                      {t("Completion Date Range")}:{" "}
                    </span>
                  )}
                  {completionDateRange}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid
              data-testid={`progression-analysis-creation-date-${progressionAnalysis?.id}`}
              item
              xs={12}
              sm={2.7}
            >
              <Tooltip title={creationDate}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>
                      {t("Creation Date")}:{" "}
                    </span>
                  )}
                  {creationDate}
                </Typography>
              </Tooltip>
            </Grid>
          </Grid>

          <Grid
            item
            xs={2}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap="4px"
          >
            {canDownload && (
              <Tooltip title={t("Download Report")}>
                <span>
                  <IconButton
                    disabled={isDownloading}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDownloadClick();
                    }}
                    sx={{
                      py: 0,
                      height: "24px",
                      color: "inherit",
                      ...noHoverBackgroundSx,
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isAnalysisValid ? (
              <Tooltip title={t("View Results")}>
                <span>
                  <a
                    href={analysisHref}
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={(e) => {
                      e.preventDefault();

                      // open report in new tab if the meta key or ctrl key is pressed
                      if (e.metaKey || e.ctrlKey)
                        window.open(analysisHref, "_blank");
                      // else open the report in the same tab
                      else navigate(analysisHref);
                    }}
                  >
                    <IconButton
                      sx={{
                        py: 0,
                        height: "24px",
                        color: "inherit",
                        ...noHoverBackgroundSx,
                      }}
                    >
                      <ViewFileIcon />
                    </IconButton>
                  </a>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={t("Invalid Report")}>
                <IconButton
                  sx={{
                    py: 0,
                    opacity: 0.5,
                    cursor: "default",
                    height: "24px",
                    color: "inherit",
                    ...noHoverBackgroundSx,
                  }}
                >
                  <AlertFileIcon />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Grid>
    </TableCard>
  );
};

export default ProgressionAnalysisCard;
